import { useEffect, useState } from "react";
import PropTypes from "prop-types";
import Chip from "@mui/material/Chip";
import Icon from "@mui/material/Icon";
import Tooltip from "@mui/material/Tooltip";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import DataTable from "examples/Tables/DataTable";
import useApi from "components/Utils/api";
import toast from "react-hot-toast";
import MerchantAssignmentDialog from "../dialogs/MerchantAssignmentDialog";
import PermissionControl from "components/Utils/PermissionControl";

// ── Cell renderers ───────────────────────────────────────────────────────────

function BusinessLineCell({ cell }) {
  return cell.value
    ? <Chip label={cell.value} size="small" />
    : <MDTypography variant="caption" color="text">All</MDTypography>;
}
BusinessLineCell.propTypes = { cell: PropTypes.shape({ value: PropTypes.string }) };

function DefaultCell({ cell }) {
  return cell.value ? <Chip label="Default" size="small" color="primary" /> : null;
}
DefaultCell.propTypes = { cell: PropTypes.shape({ value: PropTypes.bool }) };

function AssignmentStatusCell({ cell }) {
  return <Chip label={cell.value} size="small" color={cell.value === "ACTIVE" ? "success" : "default"} />;
}
AssignmentStatusCell.propTypes = { cell: PropTypes.shape({ value: PropTypes.string }) };

function DeactivateCell({ row, onDeactivate }) {
  const { id, status } = row.original;
  if (status !== "ACTIVE") return null;
  return (
    <PermissionControl permission={{ module: "SettlementRoutes", subModule: "Update" }}>
      <Tooltip title="Deactivate Assignment" arrow>
        <MDButton size="small" variant="outlined" color="error" iconOnly onClick={() => onDeactivate(id)}>
          <Icon>remove_circle</Icon>
        </MDButton>
      </Tooltip>
    </PermissionControl>
  );
}
DeactivateCell.propTypes = {
  row: PropTypes.shape({
    original: PropTypes.shape({
      id: PropTypes.string,
      status: PropTypes.string,
    }),
  }),
  onDeactivate: PropTypes.func.isRequired,
};

// ── Tab ──────────────────────────────────────────────────────────────────────

MerchantAssignmentsTab.propTypes = {
  routeId: PropTypes.string.isRequired,
};

export default function MerchantAssignmentsTab({ routeId }) {
  const api = useApi();
  const [rows, setRows] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);

  const load = () => {
    api.get(`settlement-routes/assignments?routeId=${routeId}`)
      .then((res) => setRows(res.data || []))
      .catch(() => toast.error("Failed to load assignments"));
  };

  useEffect(() => { load(); }, [routeId]);

  const handleDeactivate = async (id) => {
    try {
      await api.put(`settlement-routes/assignments/${id}/status?status=INACTIVE`);
      toast.success("Assignment deactivated");
      load();
    } catch {
      toast.error("Failed to deactivate assignment");
    }
  };

  const handleAssign = async (data) => {
    try {
      await api.post("settlement-routes/assignments", { ...data, routeId });
      toast.success("Merchants assigned");
      setDialogOpen(false);
      load();
    } catch {
      toast.error("Failed to assign merchants");
    }
  };

  const columns = [
    { Header: "MID",           accessor: "merchantMid",  width: "16%", align: "left" },
    { Header: "Merchant Name", accessor: "merchantName", width: "20%", align: "left" },
    // { Header: "Business Line", accessor: "businessLine", width: "18%", align: "left",   Cell: BusinessLineCell },
    { Header: "Default",       accessor: "isDefault",    width: "10%", align: "center", Cell: DefaultCell },
    { Header: "Status",        accessor: "status",       width: "10%", align: "left",   Cell: AssignmentStatusCell },
    {
      Header: "Actions",
      accessor: "actions",
      width: "26%",
      align: "left",
      // eslint-disable-next-line react/prop-types
      Cell: ({ row }) => <DeactivateCell row={row} onDeactivate={handleDeactivate} />,
    },
  ];

  return (
    <MDBox>
      <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <MDTypography variant="h6">Merchant Assignments</MDTypography>
        <PermissionControl permission={{ module: "SettlementRoutes", subModule: "Create" }}>
          <MDButton variant="gradient" color="info" size="small" startIcon={<Icon>add</Icon>} onClick={() => setDialogOpen(true)}>
            Assign Merchants
          </MDButton>
        </PermissionControl>
      </MDBox>
      <DataTable table={{ columns, rows }} showTotalEntries={false} isSorted={false} noEndBorder entriesPerPage={false} />
      <MerchantAssignmentDialog open={dialogOpen} routeId={routeId} onClose={() => setDialogOpen(false)} onSave={handleAssign} />
    </MDBox>
  );
}
